import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { PlatformIcon, MetricCell } from '@/components/primitives';
import {
  formatUsd,
  formatShare,
  formatCount,
  formatArpu,
  prettyQuarter,
  shortPlatform,
} from '@/lib/format';
import type { Block, ChartType } from '@/lib/workflow';
import { blockUrl, isBlockReady, resolveTokens } from '@/lib/workflow';
import { BannerBlock, type BannerSubject } from './BannerBlock';
import { TextBlock, type TextVariant } from './TextBlock';
import { Sparkline } from './Sparkline';
import { BlockSkeleton } from './BlockSkeleton';
import styles from './BlockRenderer.module.css';

export interface BlockRendererProps {
  block: Block;
  /** Resolved scope + inputs the block's tokens read from. */
  context: Record<string, string>;
  /**
   * Keys of the template's required inputs. A block referencing one of these
   * in its params waits for a value rather than fetching without it — see
   * `isBlockReady`.
   */
  requiredInputs?: readonly string[];
  /**
   * Commit edited prose for a `text` block. Absent in read-only renders
   * (export, print), which is what makes those views non-editable.
   */
  onEditContent?: (next: string) => void;
}

/** Chart colours come from the data-viz tokens so blocks match the app. */
const SERIES = {
  acquired: 'var(--metric-acquired)',
  retained: 'var(--metric-retained)',
  revenue: 'var(--accent-secondary)',
};

const REGION_COLORS: Record<string, string> = {
  UCAN: 'var(--data-1)',
  EMEA: 'var(--data-2)',
  APAC: 'var(--data-4)',
  LATAM: 'var(--data-5)',
};

/**
 * One row type for every source this renderer reads.
 *
 * Two families live here — revenue-shaped rows (`/api/titles/:id/revenue`,
 * `/api/leaderboard`) and platform-shaped ones (`/api/platforms/*`) — and a
 * block says which it expects via `options.shape`. Every field is optional
 * because this is an untyped JSON boundary: a single widened type is honest
 * about that, where two strict interfaces would only be reachable by casting.
 * The zod layer replaces this with real parsing.
 */
interface DataRow {
  // Revenue-shaped
  time_period?: string;
  region?: string;
  availability_platform?: string;
  revenue_contribution_usd?: number;
  acquired_usd?: number;
  retained_usd?: number;
  revenue_share?: number;
  title?: string;
  parrot_id?: string;
  main_genre?: string;
  subscribers_acquired?: number;
  subscribers_retained?: number;
  // Platform-shaped
  platform?: string;
  quarter?: string;
  subscribers?: number;
  arpu?: number;
  revenue?: number;
  churn_rate?: number;
  churns?: number;
  gross_additions?: number;
  net_additions?: number;
  retained_subs?: number;
}

/**
 * Summary endpoints name the same measures differently from row endpoints —
 * `/api/platforms/:name` says `total_subscribers` where the leaderboard says
 * `subscribers`. Aliasing them here means one renderer reads both shapes,
 * rather than every platform block having to know which source it came from.
 */
const SUMMARY_ALIASES: Record<string, keyof DataRow> = {
  total_subscribers: 'subscribers',
  avg_arpu: 'arpu',
  total_revenue: 'revenue',
  total_gross_additions: 'gross_additions',
};

function asRow(record: Record<string, unknown>): DataRow {
  const row: Record<string, unknown> = { ...record };
  for (const [from, to] of Object.entries(SUMMARY_ALIASES)) {
    if (row[from] !== undefined && row[to] === undefined) row[to] = row[from];
  }
  return row as DataRow;
}

function sum(rows: DataRow[], pick: (r: DataRow) => number | undefined): number {
  return rows.reduce((acc, r) => acc + (pick(r) ?? 0), 0);
}

/** Group rows by a key, summing revenue — used by the donut and table blocks. */
function groupRevenue(rows: DataRow[], key: (r: DataRow) => string | undefined) {
  return groupMetric(rows, key, (r) => r.revenue_contribution_usd);
}

/**
 * Group rows by a key, summing an arbitrary metric.
 *
 * `groupRevenue` is the revenue-shaped special case. Platform rows carry
 * `subscribers`, `revenue`, `churns` and no `revenue_contribution_usd` at all,
 * so a read that reaches for revenue on them sums a column of `undefined` and
 * renders a confident zero. This is the version those reads use.
 */
function groupMetric(
  rows: DataRow[],
  key: (r: DataRow) => string | undefined,
  metric: (r: DataRow) => number | undefined
) {
  const out = new Map<string, number>();
  for (const row of rows) {
    const k = key(row);
    if (!k) continue;
    out.set(k, (out.get(k) ?? 0) + (metric(row) ?? 0));
  }
  return [...out.entries()]
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

/**
 * Renders one block by fetching its declared source and drawing the result.
 *
 * The block describes *where* the data lives, never what the data is — so a
 * block can be re-run against a different title or quarter unchanged, and no
 * figure on the canvas originates from a language model.
 */
export function BlockRenderer({
  block,
  context,
  requiredInputs,
  onEditContent,
}: BlockRendererProps) {
  const ready = isBlockReady(block, context, requiredInputs);
  const url = ready && block.source ? blockUrl(block, context) : null;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['block', url],
    enabled: Boolean(url),
    queryFn: async () => {
      const res = await fetch(url as string);
      if (!res.ok) throw new Error(`Request failed: ${url}`);
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  /** Unwrap every response envelope the API uses. */
  const rows: DataRow[] = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.data)) return data.data;
    /* A single-record response is *one row*, not no rows. `/api/platforms/:name`
       returns one summary object, and treating that as empty is how a perfectly
       good KPI block ended up reporting "No data for the current scope" — the
       data was there, it just wasn't in an array. */
    if (typeof data === 'object') return [asRow(data as Record<string, unknown>)];
    return [];
  }, [data]);

  /* Before every data path: a text block has no source to wait on, no request
     to make and no error state to report. */
  if (block.type === 'text') {
    const variant = (block.options?.variant as TextVariant) ?? 'body';
    return (
      <TextBlock
        content={block.content ?? ''}
        variant={variant}
        onChange={onEditContent}
        placeholder={
          variant === 'heading' ? 'Section heading…' : 'Write a summary, caveat or note…'
        }
      />
    );
  }

  if (!ready) {
    return (
      <div className={styles.placeholder}>
        <p className={styles.placeholderText}>
          Awaiting input — choose a subject for this workflow to render “{block.title}”.
        </p>
      </div>
    );
  }

  if (isLoading) {
    /* Shaped like the block that is arriving, so the space it will occupy is
       already reserved and nothing jumps when the data lands. */
    return <BlockSkeleton type={block.type} />;
  }

  if (isError) {
    return (
      <div className={styles.placeholder}>
        <p className={styles.placeholderText}>
          Couldn’t load this block. Check the API server is running.
        </p>
      </div>
    );
  }

  /* Before the row check: both banner sources return a single object, not a
     row list, so `rows` is legitimately empty for them. */
  if (block.type === 'banner') {
    const subject = (Array.isArray(data) ? data[0] : (data?.data ?? data)) as
      | BannerSubject
      | undefined;
    if (!subject || typeof subject !== 'object') {
      return (
        <div className={styles.placeholder}>
          <p className={styles.placeholderText}>No details for this subject.</p>
        </div>
      );
    }
    return <BannerBlock subject={subject} />;
  }

  if (rows.length === 0) {
    return (
      <div className={styles.placeholder}>
        <p className={styles.placeholderText}>No data for the current scope.</p>
      </div>
    );
  }

  /* Platform sources carry a different row shape (subscribers/ARPU/churn)
     rather than revenue contributions, so they get their own renderers. The
     block declares `shape` explicitly instead of the renderer sniffing fields
     — a source that happens to be missing a column should render empty, not
     silently fall through to the wrong reader. */
  if (block.options?.shape === 'platform') {
    switch (block.type) {
      case 'kpis':
        return <PlatformKpiBlock rows={rows} block={block} context={context} />;
      case 'chart':
      case 'timeseries':
      case 'donut':
        return <PlatformChartBlock rows={rows} block={block} />;
      case 'table':
        return <PlatformTableBlock rows={rows} block={block} />;
      /* Notes read the rows themselves rather than going through a shaped
         renderer, so there is no platform variant to dispatch to — and
         falling through to `default` here silently rendered nothing. */
      case 'note':
        return <NoteBlock rows={rows} block={block} context={context} />;
      default:
        return null;
    }
  }

  switch (block.type) {
    case 'kpis':
      return <KpiBlock rows={rows} block={block} context={context} />;
    /* One entry point: `chart` plus the two legacy aliases still found in
       saved workflows. `chartSpec` sorts out which shape to draw. */
    case 'chart':
    case 'timeseries':
    case 'donut':
      return <ChartBlock rows={rows} block={block} />;
    case 'split':
      return <SplitBlock rows={rows} />;
    case 'table':
      return <TableBlock rows={rows} block={block} />;
    case 'note':
      return <NoteBlock rows={rows} block={block} context={context} />;
    default:
      return null;
  }
}

// ─── Block types ───────────────────────────────────────────────────────────

interface Stat {
  label: string;
  value: string;
  sub?: string;
  /** Quarterly series behind the figure, oldest first. */
  series?: number[];
  /** Change across the series, as a fraction. */
  delta?: number;
}

/**
 * Quarterly totals for a metric, oldest first.
 *
 * Rows arrive split by region and platform, so a quarter appears many times
 * and has to be summed before it means anything as a series.
 */
function quarterly(rows: DataRow[], pick: (r: DataRow) => number | undefined): number[] {
  const byQuarter = new Map<string, number>();
  for (const row of rows) {
    if (!row.time_period) continue;
    byQuarter.set(row.time_period, (byQuarter.get(row.time_period) ?? 0) + (pick(row) ?? 0));
  }
  return [...byQuarter.entries()]
    .sort(([a], [b]) => a.localeCompare(b)) // "2020 Q1" sorts correctly as text
    .map(([, value]) => value);
}

/** Running total — what a "total to date" figure actually traced out. */
function cumulative(values: number[]): number[] {
  let running = 0;
  return values.map((v) => (running += v));
}

/**
 * Quarter-over-quarter change, from the *per-quarter* series.
 *
 * Deliberately not first-to-last. The sparkline for a total runs cumulatively,
 * and a cumulative series always starts near zero — so comparing its ends
 * measures "the lifetime total against the opening quarter" and prints
 * something like +33688%. True, arithmetically, and meaningless to a reader.
 *
 * Suppressed entirely when the prior quarter is negligible against the series
 * peak: dividing by a near-zero base produces a huge number that says more
 * about the denominator than the trend.
 */
function periodChange(series: number[]): number | undefined {
  if (series.length < 2) return undefined;
  const previous = series[series.length - 2];
  const latest = series[series.length - 1];
  const peak = Math.max(...series.map(Math.abs));
  if (!previous || Math.abs(previous) < peak * 0.01) return undefined;
  return (latest - previous) / Math.abs(previous);
}

function toneOf(delta: number | undefined): 'up' | 'down' | 'flat' {
  if (delta === undefined || Math.abs(delta) < 0.005) return 'flat';
  return delta > 0 ? 'up' : 'down';
}

function KpiBlock({
  rows,
  block,
  context,
}: {
  rows: DataRow[];
  block: Block;
  context: Record<string, string>;
}) {
  const revenue = sum(rows, (r) => r.revenue_contribution_usd);
  const acquired = sum(rows, (r) => r.acquired_usd);
  const retained = sum(rows, (r) => r.retained_usd);
  const total = acquired + retained;
  const quarters = [...new Set(rows.map((r) => r.time_period).filter(Boolean))];
  const regions = new Set(rows.map((r) => r.region).filter(Boolean)).size;

  /* Rank comes from a different endpoint to the figures, so a block opts in by
     naming one. Only fetched when declared — most KPI blocks have no ranking
     to show and shouldn't pay for a request. */
  const rankSource =
    typeof block.options?.rankSource === 'string'
      ? resolveTokens(block.options.rankSource, context)
      : null;

  const { data: rankData } = useQuery({
    queryKey: ['rank', rankSource],
    enabled: Boolean(rankSource) && !rankSource!.includes('//'),
    queryFn: async () => {
      const res = await fetch(rankSource as string);
      if (!res.ok) throw new Error('rank');
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  /* Best rank across platforms — a title ranked #2 somewhere and #40 elsewhere
     is meaningfully a #2. The endpoint carries a single quarter, so there is
     no prior rank to compare against and no rank delta is shown. */
  const rank = useMemo(() => {
    const list: DataRow[] = Array.isArray(rankData)
      ? rankData
      : Array.isArray(rankData?.data)
        ? rankData.data
        : [];
    const ranks = list
      .map((r) => (r as { title_rank?: number }).title_rank)
      .filter((r): r is number => typeof r === 'number' && r > 0);
    return ranks.length > 0 ? Math.min(...ranks) : null;
  }, [rankData]);

  /* Sparkline and delta read different series on purpose: the curve shows how
     the total accumulated, the delta shows the most recent quarter's move. */
  const revenueQuarterly = quarterly(rows, (r) => r.revenue_contribution_usd);
  const revenueSeries = cumulative(revenueQuarterly);
  const acquiredSeries = quarterly(rows, (r) => r.acquired_usd);
  const retainedSeries = quarterly(rows, (r) => r.retained_usd);

  const stats: Stat[] = [
    {
      label: 'Total revenue',
      value: formatUsd(revenue),
      series: revenueSeries,
      delta: periodChange(revenueQuarterly),
    },
  ];
  if (total > 0) {
    stats.push({
      label: 'Acquisition',
      value: `${((acquired / total) * 100).toFixed(0)}%`,
      sub: formatUsd(acquired),
      series: acquiredSeries,
      delta: periodChange(acquiredSeries),
    });
    stats.push({
      label: 'Retention',
      value: `${((retained / total) * 100).toFixed(0)}%`,
      sub: formatUsd(retained),
      series: retainedSeries,
      delta: periodChange(retainedSeries),
    });
  }

  /* Coverage describes the scope, so it belongs in the caption rather than
     standing among the figures. "Rows: 108" was never a business number — it
     counted database rows on a page meant for a client. */
  const coverage = [
    quarters.length > 0
      ? `${quarters.length} quarter${quarters.length === 1 ? '' : 's'}`
      : null,
    regions > 0 ? `${regions} region${regions === 1 ? '' : 's'}` : null,
    context.region ? context.region : 'All regions',
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div>
      <p className={styles.kpiScope}>{coverage}</p>
      <div className={styles.kpiRow}>
        {stats.map((stat) => (
          <div key={stat.label} className={styles.kpi}>
            <span className={styles.kpiLabel}>{stat.label}</span>
            <span className={styles.kpiFigure}>
              <span className={styles.kpiValue}>{stat.value}</span>
              {/* Rank belongs to the headline figure only — repeating it on
                  every stat would imply each one is ranked. */}
              {rank !== null && stat.label === 'Total revenue' && (
                <span className={styles.kpiRank}>#{rank}</span>
              )}
            </span>
            <span className={styles.kpiTrend}>
              {stat.series && stat.series.length > 1 && (
                <Sparkline values={stat.series} tone={toneOf(stat.delta)} />
              )}
              {stat.delta !== undefined && Math.abs(stat.delta) >= 0.005 && (
                <span className={styles.kpiDelta} data-tone={toneOf(stat.delta)}>
                  {stat.delta > 0 ? '▲' : '▼'} {Math.abs(stat.delta * 100).toFixed(0)}%
                </span>
              )}
            </span>
            {stat.sub && <span className={styles.kpiSub}>{stat.sub}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * How a block wants to be drawn.
 *
 * Two families, and `groupBy` decides which: with it, the chart compares
 * categories (regions, platforms, genres); without it, the chart runs over
 * quarters. That single rule is also what lets the legacy type names normalise
 * cleanly — every `donut` block carries a `groupBy` and every `timeseries`
 * block doesn't.
 */
function chartSpec(block: Block): { chartType: ChartType; categorical: boolean } {
  const declared = block.options?.chartType;
  const legacy: Record<string, ChartType> = { donut: 'donut', timeseries: 'area' };

  const chartType: ChartType =
    typeof declared === 'string' && ['line', 'bar', 'area', 'donut'].includes(declared)
      ? (declared as ChartType)
      : (legacy[block.type] ?? 'bar');

  /* A donut is categorical whatever else is set — it has no time axis. */
  const categorical = chartType === 'donut' || typeof block.options?.groupBy === 'string';
  return { chartType, categorical };
}

/** Which column a categorical chart groups on. */
function categoryKey(groupBy: string) {
  return (r: DataRow) =>
    groupBy === 'platform'
      ? r.availability_platform
      : groupBy === 'genre'
        ? r.main_genre
        : r.region;
}

const sliceColour = (name: string, i: number) =>
  REGION_COLORS[name] ?? `var(--data-${(i % 10) + 1})`;

/**
 * Categories as horizontal bars.
 *
 * Horizontal because category names here are long — "Amazon Prime Video",
 * "Science Fiction" — and rotating them under a vertical axis costs more
 * legibility than the extra height does. Also the reason a bar is often the
 * right answer over a donut: ten platform slices in a ring are unreadable, ten
 * bars are fine.
 */
function CategoryBarBlock({ rows, block }: { rows: DataRow[]; block: Block }) {
  const groupBy = (block.options?.groupBy as string) ?? 'region';
  const limit = typeof block.options?.limit === 'number' ? block.options.limit : 12;
  const bars = useMemo(
    () => groupRevenue(rows, categoryKey(groupBy)).slice(0, limit),
    [rows, groupBy, limit]
  );

  if (bars.length === 0) {
    return <p className={styles.placeholderText}>No data for the current scope.</p>;
  }

  return (
    <div className={styles.chart}>
      <ResponsiveContainer width="100%" height={Math.max(120, bars.length * 34 + 16)}>
        <BarChart data={bars} layout="vertical" margin={{ top: 0, right: 12, bottom: 0, left: 0 }}>
          <XAxis type="number" hide />
          <YAxis
            type="category"
            dataKey="name"
            width={124}
            tick={{ fill: 'var(--text-secondary)', fontSize: 12 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v: string) => shortPlatform(v)}
          />
          <RechartsTooltip
            contentStyle={tooltipStyle}
            cursor={{ fill: 'var(--bg-hover)' }}
            formatter={(value: number) => formatUsd(value)}
          />
          <Bar dataKey="value" name="Revenue" radius={[0, 4, 4, 0]} barSize={18}>
            {bars.map((bar, i) => (
              <Cell key={bar.name} fill={sliceColour(bar.name, i)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Acquisition and retention over quarters, as area, line or bar. */
function TemporalChartBlock({
  rows,
  chartType,
}: {
  rows: DataRow[];
  chartType: ChartType;
}) {
  const series = useMemo(() => {
    const byQuarter = new Map<string, { acquired: number; retained: number }>();
    for (const row of rows) {
      if (!row.time_period) continue;
      const entry = byQuarter.get(row.time_period) ?? { acquired: 0, retained: 0 };
      entry.acquired += row.acquired_usd ?? 0;
      entry.retained += row.retained_usd ?? 0;
      byQuarter.set(row.time_period, entry);
    }
    return [...byQuarter.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([quarter, v]) => ({ quarter: prettyQuarter(quarter), ...v }));
  }, [rows]);

  if (series.length === 0) {
    return <p className={styles.placeholderText}>No quarterly data for this scope.</p>;
  }

  const axes = (
    <>
      <CartesianGrid stroke="var(--border-subtle)" vertical={false} />
      <XAxis
        dataKey="quarter"
        tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
        tickLine={false}
        axisLine={{ stroke: 'var(--border-subtle)' }}
        minTickGap={24}
      />
      <YAxis
        tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
        tickLine={false}
        axisLine={false}
        tickFormatter={(v: number) => formatUsd(v)}
        width={64}
      />
      <RechartsTooltip
        contentStyle={tooltipStyle}
        formatter={(value: number, name: string) => [formatUsd(value), name]}
      />
      <Legend wrapperStyle={{ fontSize: 12, color: 'var(--text-muted)' }} />
    </>
  );

  return (
    <div className={styles.chart}>
      <ResponsiveContainer width="100%" height={240}>
        {chartType === 'line' ? (
          /* Lines are not stacked — stacking them would draw a top line that
             is the total while looking like a series in its own right. */
          <LineChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
            {axes}
            <Line
              type="monotone"
              dataKey="retained"
              name="Retained"
              stroke={SERIES.retained}
              strokeWidth={2}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="acquired"
              name="Acquired"
              stroke={SERIES.acquired}
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        ) : chartType === 'bar' ? (
          <BarChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
            {axes}
            <Bar dataKey="retained" name="Retained" stackId="q" fill={SERIES.retained} />
            <Bar dataKey="acquired" name="Acquired" stackId="q" fill={SERIES.acquired} />
          </BarChart>
        ) : (
          <AreaChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
            {axes}
            <Area
              type="monotone"
              dataKey="retained"
              name="Retained"
              stackId="1"
              stroke={SERIES.retained}
              fill={SERIES.retained}
              fillOpacity={0.25}
            />
            <Area
              type="monotone"
              dataKey="acquired"
              name="Acquired"
              stackId="1"
              stroke={SERIES.acquired}
              fill={SERIES.acquired}
              fillOpacity={0.35}
            />
          </AreaChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

/** The one entry point for every revenue-shaped chart. */
function ChartBlock({ rows, block }: { rows: DataRow[]; block: Block }) {
  const { chartType, categorical } = chartSpec(block);
  if (chartType === 'donut') return <DonutBlock rows={rows} block={block} />;
  if (categorical) return <CategoryBarBlock rows={rows} block={block} />;
  return <TemporalChartBlock rows={rows} chartType={chartType} />;
}


function DonutBlock({ rows, block }: { rows: DataRow[]; block: Block }) {
  const groupBy = (block.options?.groupBy as string) ?? 'region';
  const slices = useMemo(
    () =>
      groupRevenue(rows, (r) =>
        groupBy === 'platform'
          ? r.availability_platform
          : groupBy === 'genre'
            ? r.main_genre
            : r.region
      ),
    [rows, groupBy]
  );

  const total = slices.reduce((acc, s) => acc + s.value, 0);

  return (
    <div className={styles.donutWrap}>
      <div className={styles.donutChart}>
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie
              data={slices}
              dataKey="value"
              nameKey="name"
              innerRadius={52}
              outerRadius={80}
              paddingAngle={2}
              stroke="none"
            >
              {slices.map((slice, i) => (
                <Cell
                  key={slice.name}
                  fill={REGION_COLORS[slice.name] ?? `var(--data-${(i % 10) + 1})`}
                />
              ))}
            </Pie>
            <RechartsTooltip
              contentStyle={tooltipStyle}
              formatter={(value: number) => formatUsd(value)}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <ul className={styles.legend}>
        {slices.map((slice, i) => (
          <li key={slice.name} className={styles.legendRow}>
            <span
              className={styles.legendSwatch}
              style={{
                background: REGION_COLORS[slice.name] ?? `var(--data-${(i % 10) + 1})`,
              }}
            />
            <span className={styles.legendName}>{shortPlatform(slice.name)}</span>
            <span className={styles.legendValue}>{formatUsd(slice.value)}</span>
            <span className={styles.legendPct}>
              {total > 0 ? formatShare(slice.value / total, 1) : '—'}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SplitBlock({ rows }: { rows: DataRow[] }) {
  const acquired = sum(rows, (r) => r.acquired_usd);
  const retained = sum(rows, (r) => r.retained_usd);
  const total = acquired + retained;
  if (total <= 0) return <p className={styles.placeholderText}>No acquisition data.</p>;

  const acqPct = (acquired / total) * 100;
  const data = [{ name: 'split', acquired, retained }];

  return (
    <div className={styles.splitWrap}>
      <ResponsiveContainer width="100%" height={56}>
        <BarChart data={data} layout="vertical" margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
          <XAxis type="number" hide />
          <YAxis type="category" dataKey="name" hide />
          <RechartsTooltip
            contentStyle={tooltipStyle}
            formatter={(value: number, name: string) => [formatUsd(value), name]}
          />
          <Bar dataKey="acquired" name="Acquired" stackId="s" fill={SERIES.acquired} radius={[4, 0, 0, 4]} />
          <Bar dataKey="retained" name="Retained" stackId="s" fill={SERIES.retained} radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>

      <div className={styles.splitLegend}>
        <span className={styles.splitAcq}>
          {acqPct.toFixed(0)}% acquisition · {formatUsd(acquired)}
        </span>
        <span className={styles.splitRet}>
          {(100 - acqPct).toFixed(0)}% retention · {formatUsd(retained)}
        </span>
      </div>
    </div>
  );
}

function TableBlock({ rows, block }: { rows: DataRow[]; block: Block }) {
  const limit = (block.options?.limit as number) ?? 10;
  const visible = rows.slice(0, limit);
  const max = Math.max(...visible.map((r) => r.revenue_contribution_usd ?? 0), 1);

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.th}>#</th>
            <th className={styles.th}>Title</th>
            <th className={styles.th}>Platform</th>
            <th className={styles.th}>Revenue</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((row, i) => (
            <tr key={`${row.parrot_id ?? row.title}-${i}`} className={styles.tr}>
              <td className={styles.td}>{i + 1}</td>
              <td className={styles.td}>{row.title ?? '—'}</td>
              <td className={styles.td}>
                {row.availability_platform ? (
                  <span className={styles.platformCell}>
                    <PlatformIcon name={row.availability_platform} size={18} />
                    {shortPlatform(row.availability_platform)}
                  </span>
                ) : (
                  '—'
                )}
              </td>
              <td className={styles.td}>
                <MetricCell
                  value={formatUsd(row.revenue_contribution_usd ?? 0)}
                  fraction={(row.revenue_contribution_usd ?? 0) / max}
                  tone="revenue"
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * A computed sentence — every number is derived from the fetched rows, never
 * written by a model. The agent may choose to add a note block; it cannot
 * choose what the note says.
 */
/**
 * A computed read of the section above it.
 *
 * Every figure is derived from the same rows the charts draw, so a note can be
 * specific without anything being authored — "69% of revenue is earned outside
 * UCAN" is an insight, not a placeholder, and it cannot be wrong for a subject
 * the way a hand-written sentence in a template would be.
 *
 * `options.insight` picks which read. They exist because a deliverable makes a
 * different point after each section, and one fixed summary repeated four
 * times is just noise.
 */
function NoteBlock({
  rows,
  block,
  context,
}: {
  rows: DataRow[];
  block: Block;
  context: Record<string, string>;
}) {
  const insight = typeof block.options?.insight === 'string' ? block.options.insight : 'summary';

  const revenue = sum(rows, (r) => r.revenue_contribution_usd);
  const acquired = sum(rows, (r) => r.acquired_usd);
  const retained = sum(rows, (r) => r.retained_usd);
  const total = acquired + retained;

  // ─── Geography: the domestic/international split clients always ask for ───
  if (insight === 'geography') {
    const regions = groupRevenue(rows, (r) => r.region);
    const all = regions.reduce((acc, r) => acc + r.value, 0);
    if (all <= 0) return <p className={styles.note}>No regional revenue in this scope.</p>;
    const ucan = regions.find((r) => r.name === 'UCAN')?.value ?? 0;
    const international = all - ucan;
    const top = regions.filter((r) => r.name !== 'UCAN')[0];

    return (
      <p className={styles.note}>
        <strong>{formatShare(international / all, 0)}</strong> of revenue is earned
        outside UCAN
        {top && (
          <>
            , led by <strong>{top.name}</strong> at {formatShare(top.value / all, 0)}
          </>
        )}
        . Domestic contributes <strong>{formatShare(ucan / all, 0)}</strong>
        {ucan > international
          ? ' — this is a domestically weighted title.'
          : ' — the majority of its value is international.'}
      </p>
    );
  }

  // ─── Role: what the title does for a platform ─────────────────────────────
  if (insight === 'role') {
    if (total <= 0) return <p className={styles.note}>No acquisition data in this scope.</p>;
    const retentionLed = retained > acquired;
    const share = retentionLed ? retained / total : acquired / total;

    return (
      <p className={styles.note}>
        This is a <strong>{retentionLed ? 'retention' : 'acquisition'}</strong> title:{' '}
        <strong>{formatShare(share, 0)}</strong> of its revenue comes from subscribers it{' '}
        {retentionLed ? 'kept' : 'won'} ({formatUsd(retentionLed ? retained : acquired)}).{' '}
        {retentionLed
          ? 'Value of that kind argues for renewal and library cost rather than launch spend.'
          : 'Value of that kind argues for a launch window and marketing behind it.'}
      </p>
    );
  }

  // ─── Platforms: spread and concentration ──────────────────────────────────
  if (insight === 'platforms') {
    const platforms = groupRevenue(rows, (r) => r.availability_platform);
    const all = platforms.reduce((acc, p) => acc + p.value, 0);
    if (platforms.length === 0 || all <= 0) {
      return <p className={styles.note}>No platform revenue in this scope.</p>;
    }
    const lead = platforms[0];

    return (
      <p className={styles.note}>
        Carried on <strong>{platforms.length}</strong> platform
        {platforms.length === 1 ? '' : 's'}, with{' '}
        <strong>{shortPlatform(lead.name)}</strong> taking{' '}
        <strong>{formatShare(lead.value / all, 0)}</strong> of the revenue
        {platforms.length > 1 && lead.value / all > 0.9
          ? ' — effectively a single-platform title.'
          : platforms.length > 1
            ? ` (${formatUsd(lead.value)}).`
            : '.'}
      </p>
    );
  }

  // ─── Comps: where the subject sits in its own comparable set ──────────────
  if (insight === 'comps') {
    const byTitle = new Map<string, { title: string; id?: string; value: number }>();
    for (const row of rows) {
      if (!row.title) continue;
      const entry = byTitle.get(row.title) ?? {
        title: row.title,
        id: row.parrot_id,
        value: 0,
      };
      entry.value += row.revenue_contribution_usd ?? 0;
      byTitle.set(row.title, entry);
    }
    const ranked = [...byTitle.values()].sort((a, b) => b.value - a.value);
    const position = ranked.findIndex((t) => t.id && t.id === context.titleId);

    if (position < 0) {
      return (
        <p className={styles.note}>
          <strong>{ranked.length}</strong> titles share this demand band. The subject sits
          outside the top {ranked.length} on revenue.
        </p>
      );
    }

    const above = ranked[position - 1];
    const below = ranked[position + 1];

    return (
      <p className={styles.note}>
        Ranks <strong>#{position + 1}</strong> of <strong>{ranked.length}</strong> titles in
        the same demand band
        {above && (
          <>
            , behind <strong>{above.title}</strong> ({formatUsd(above.value)})
          </>
        )}
        {below && (
          <>
            {' '}and ahead of <strong>{below.title}</strong> ({formatUsd(below.value)})
          </>
        )}
        .
      </p>
    );
  }

  // ─── Concentration: how much of the whole sits at the top ─────────────────
  /* The question every catalogue review answers first. A platform with 400
     titles where six carry half the revenue is a different asset from one
     where value is spread evenly, and the difference decides whether you buy
     hits or buy volume. */
  if (insight === 'concentration') {
    const titles = groupRevenue(rows, (r) => r.title);
    const all = titles.reduce((acc, t) => acc + t.value, 0);
    if (titles.length === 0 || all <= 0) {
      return <p className={styles.note}>No revenue in this scope.</p>;
    }
    const topN = Math.min(3, titles.length);
    const top = titles.slice(0, topN).reduce((acc, t) => acc + t.value, 0);
    const half = titles.findIndex(
      (_, i) => titles.slice(0, i + 1).reduce((a, t) => a + t.value, 0) >= all / 2
    );

    return (
      <p className={styles.note}>
        The top <strong>{topN}</strong> of <strong>{titles.length}</strong> titles carry{' '}
        <strong>{formatShare(top / all, 0)}</strong> of revenue ({formatUsd(top)}), led by{' '}
        <strong>{titles[0].name}</strong>.
        {half >= 0 && (
          <>
            {' '}
            Half the value sits in the top <strong>{half + 1}</strong>
            {half + 1 <= 5 ? ' — this is a hit-driven catalogue.' : ' — value is broadly spread.'}
          </>
        )}
      </p>
    );
  }

  // ─── Genres: what the mix actually rewards ────────────────────────────────
  if (insight === 'genres') {
    const genres = groupRevenue(rows, (r) => r.main_genre);
    const all = genres.reduce((acc, g) => acc + g.value, 0);
    if (genres.length === 0 || all <= 0) {
      return <p className={styles.note}>No genre revenue in this scope.</p>;
    }
    const lead = genres[0];
    const topThree = genres.slice(0, 3).reduce((acc, g) => acc + g.value, 0);

    return (
      <p className={styles.note}>
        <strong>{lead.name}</strong> leads at <strong>{formatShare(lead.value / all, 0)}</strong>{' '}
        of revenue ({formatUsd(lead.value)}) across <strong>{genres.length}</strong> genres.
        {genres.length > 3 && (
          <>
            {' '}
            The top three together take <strong>{formatShare(topThree / all, 0)}</strong>
            {topThree / all > 0.8
              ? ' — the mix is narrower than the catalogue size suggests.'
              : '.'}
          </>
        )}
      </p>
    );
  }

  // ─── Market: the platform leaderboard, read as a market structure ─────────
  /* Platform-shaped rows carry `subscribers`, not `revenue_contribution_usd`,
     so these reads group their own metric rather than going through
     `groupRevenue`. Mixing the two is what makes a note render zeros. */
  if (insight === 'market') {
    const byPlatform = groupMetric(rows, (r) => r.platform, (r) => r.subscribers);
    const all = byPlatform.reduce((acc, p) => acc + p.value, 0);
    if (byPlatform.length === 0 || all <= 0) {
      return <p className={styles.note}>No subscriber data in this scope.</p>;
    }
    const lead = byPlatform[0];
    const topThree = byPlatform.slice(0, 3).reduce((acc, p) => acc + p.value, 0);

    return (
      <p className={styles.note}>
        <strong>{shortPlatform(lead.name)}</strong> leads with{' '}
        <strong>{formatCount(lead.value)}</strong> subscribers —{' '}
        <strong>{formatShare(lead.value / all, 0)}</strong> of the{' '}
        {formatCount(all)} across <strong>{byPlatform.length}</strong> platforms.
        {byPlatform.length > 3 && (
          <>
            {' '}
            The top three hold <strong>{formatShare(topThree / all, 0)}</strong>
            {topThree / all > 0.7 ? ' — a concentrated market.' : ' — a fragmented market.'}
          </>
        )}
      </p>
    );
  }

  // ─── Churn: what the leak costs against what was won ──────────────────────
  if (insight === 'churn') {
    const gross = sum(rows, (r) => r.gross_additions);
    const churned = sum(rows, (r) => r.churns);
    const net = sum(rows, (r) => r.net_additions);
    if (gross <= 0 && churned <= 0) {
      return <p className={styles.note}>No additions or churn in this scope.</p>;
    }
    /* Worst rate, not worst absolute — the biggest platform churns the most
       subscribers almost by definition, which says nothing about its health. */
    const worst = [...rows]
      .filter((r) => r.platform && (r.churn_rate ?? 0) > 0)
      .sort((a, b) => (b.churn_rate ?? 0) - (a.churn_rate ?? 0))[0];

    return (
      <p className={styles.note}>
        The market won <strong>{formatCount(gross)}</strong> subscribers and lost{' '}
        <strong>{formatCount(churned)}</strong> — a net{' '}
        <strong>{net >= 0 ? '+' : '−'}{formatCount(Math.abs(net))}</strong>.{' '}
        {gross > 0 && (
          <>
            Churn absorbs <strong>{formatShare(churned / gross, 0)}</strong> of everything won.{' '}
          </>
        )}
        {worst && (
          <>
            Highest rate is <strong>{shortPlatform(worst.platform ?? '')}</strong> at{' '}
            {formatShare(worst.churn_rate ?? 0, 1)}.
          </>
        )}
      </p>
    );
  }

  // ─── Growth: the trajectory across every quarter on record ────────────────
  if (insight === 'growth') {
    const series = [...rows]
      .filter((r) => r.quarter)
      .sort((a, b) => (a.quarter ?? '').localeCompare(b.quarter ?? ''));
    const points = series.map((r) => r.subscribers ?? 0).filter((v) => v > 0);
    if (points.length < 2) {
      return <p className={styles.note}>Not enough history to read a trajectory.</p>;
    }
    const first = points[0];
    const last = points[points.length - 1];
    const change = (last - first) / first;
    const latest = periodChange(points);

    return (
      <p className={styles.note}>
        Subscribers moved from <strong>{formatCount(first)}</strong> to{' '}
        <strong>{formatCount(last)}</strong> across <strong>{points.length}</strong> quarters —{' '}
        <strong>{change >= 0 ? '+' : '−'}{formatShare(Math.abs(change), 0)}</strong> overall.
        {latest !== undefined && (
          <>
            {' '}
            The latest quarter moved{' '}
            <strong>
              {latest >= 0 ? '+' : '−'}
              {formatShare(Math.abs(latest), 1)}
            </strong>
            {Math.abs(latest) < 0.01
              ? ' — growth has flattened.'
              : latest < 0
                ? ' — the base is contracting.'
                : '.'}
          </>
        )}
      </p>
    );
  }

  // ─── Footprint: where a platform's base and money actually sit ────────────
  if (insight === 'footprint') {
    const byRegion = groupMetric(rows, (r) => r.region, (r) => r.subscribers);
    const all = byRegion.reduce((acc, r) => acc + r.value, 0);
    if (byRegion.length === 0 || all <= 0) {
      return <p className={styles.note}>No regional data in this scope.</p>;
    }
    const lead = byRegion[0];
    const revenueByRegion = groupMetric(rows, (r) => r.region, (r) => r.revenue);
    const topRevenue = revenueByRegion[0];

    return (
      <p className={styles.note}>
        <strong>{lead.name}</strong> holds <strong>{formatShare(lead.value / all, 0)}</strong> of
        the subscriber base across <strong>{byRegion.length}</strong> regions.
        {topRevenue && topRevenue.name !== lead.name && (
          <>
            {' '}
            Revenue leads elsewhere — <strong>{topRevenue.name}</strong> earns most, so ARPU
            differs sharply by market.
          </>
        )}
        {topRevenue && topRevenue.name === lead.name && (
          <> Revenue concentrates in the same market, so the base and the money agree.</>
        )}
      </p>
    );
  }

  // ─── Summary (default) ────────────────────────────────────────────────────
  // A type predicate, not `.filter(Boolean)` — the latter doesn't narrow.
  const quarters = [
    ...new Set(rows.map((r) => r.time_period).filter((q): q is string => Boolean(q))),
  ].sort();
  const regions = groupRevenue(rows, (r) => r.region);
  const topRegion = regions[0];

  return (
    <p className={styles.note}>
      Generated <strong>{formatUsd(revenue)}</strong> across{' '}
      <strong>{quarters.length}</strong> quarters
      {quarters.length > 0 && (
        <>
          {' '}
          ({prettyQuarter(quarters[0])} – {prettyQuarter(quarters[quarters.length - 1])})
        </>
      )}
      .{' '}
      {total > 0 && (
        <>
          Revenue splits <strong>{((acquired / total) * 100).toFixed(0)}%</strong> to
          acquisition and <strong>{((retained / total) * 100).toFixed(0)}%</strong> to
          retention.{' '}
        </>
      )}
      {topRegion && (
        <>
          Strongest region is <strong>{topRegion.name}</strong> at{' '}
          {formatUsd(topRegion.value)}.
        </>
      )}
    </p>
  );
}

const tooltipStyle = {
  background: 'var(--bg-elevated)',
  border: '1px solid var(--border-default)',
  borderRadius: 8,
  fontSize: 12,
  color: 'var(--text-primary)',
} as const;

// ─── Platform-shaped blocks ────────────────────────────────────────────────
//
// Sources under /api/platforms/* return subscribers, ARPU, revenue and churn
// rather than revenue contributions. A block opts in with `shape: 'platform'`.

/** How each platform metric is labelled and formatted. */
const PLATFORM_METRICS: Record<
  string,
  { label: string; format: (v: number) => string; pick: (r: DataRow) => number | undefined }
> = {
  subscribers: { label: 'Subscribers', format: formatCount, pick: (r) => r.subscribers },
  revenue: { label: 'Revenue', format: formatUsd, pick: (r) => r.revenue },
  arpu: { label: 'ARPU', format: formatArpu, pick: (r) => r.arpu },
  gross_additions: { label: 'Gross additions', format: formatCount, pick: (r) => r.gross_additions },
  net_additions: { label: 'Net additions', format: formatCount, pick: (r) => r.net_additions },
  churns: { label: 'Churns', format: formatCount, pick: (r) => r.churns },
  churn_rate: { label: 'Churn rate', format: (v) => `${v.toFixed(2)}%`, pick: (r) => r.churn_rate },
};

function metricOf(block: Block, fallback = 'subscribers') {
  const key = typeof block.options?.metric === 'string' ? block.options.metric : fallback;
  return PLATFORM_METRICS[key] ?? PLATFORM_METRICS[fallback];
}

/**
 * Narrow a leaderboard response to one platform when the block names one.
 * `/api/platforms/leaderboard` has no platform filter, so the selection is
 * applied here rather than by the request.
 */
function forPlatform(rows: DataRow[], block: Block, context: Record<string, string>): DataRow[] {
  const raw = typeof block.options?.platform === 'string' ? block.options.platform : '';
  const wanted = resolveTokens(raw, context);
  if (!wanted) return rows;
  const matched = rows.filter((r) => r.platform === wanted);
  return matched.length > 0 ? matched : rows;
}

function PlatformKpiBlock({
  rows,
  block,
  context,
}: {
  rows: DataRow[];
  block: Block;
  context: Record<string, string>;
}) {
  const scoped = forPlatform(rows, block, context);
  const single = scoped.length === 1;

  /* One platform reads as a scorecard; many read as a market total. ARPU is
     the exception — summing it is meaningless, so it is weighted by
     subscribers, matching how the platforms leaderboard computes it. */
  const subscribers = sum(scoped, (r) => r.subscribers);
  const revenue = sum(scoped, (r) => r.revenue);
  const churns = sum(scoped, (r) => r.churns);
  const grossAdds = sum(scoped, (r) => r.gross_additions);
  const arpu = single
    ? (scoped[0].arpu ?? 0)
    : subscribers > 0
      ? revenue / subscribers
      : 0;
  const churnRate = single
    ? (scoped[0].churn_rate ?? 0)
    : subscribers > 0
      ? (churns / subscribers) * 100
      : 0;

  const stats = [
    { label: 'Subscribers', value: formatCount(subscribers) },
    { label: 'Revenue', value: formatUsd(revenue) },
    { label: 'ARPU', value: formatArpu(arpu), sub: single ? undefined : 'Subscriber-weighted' },
    { label: 'Churn rate', value: `${churnRate.toFixed(2)}%` },
    { label: 'Gross additions', value: formatCount(grossAdds) },
  ];

  return (
    <div className={styles.kpiRow}>
      {stats.map((stat) => (
        <div key={stat.label} className={styles.kpi}>
          <span className={styles.kpiLabel}>{stat.label}</span>
          <span className={styles.kpiValue}>{stat.value}</span>
          {stat.sub && <span className={styles.kpiSub}>{stat.sub}</span>}
        </div>
      ))}
    </div>
  );
}

/**
 * Platform-shaped charts, same four shapes as the revenue side.
 *
 * The metric is whichever `options.metric` names (subscribers, ARPU, churn…),
 * so a bar here compares platforms or regions on that metric rather than on
 * revenue.
 */
function PlatformCategoryBarBlock({ rows, block }: { rows: DataRow[]; block: Block }) {
  const metric = metricOf(block);
  const groupBy = typeof block.options?.groupBy === 'string' ? block.options.groupBy : 'region';
  const limit = typeof block.options?.limit === 'number' ? block.options.limit : 12;

  const bars = useMemo(() => {
    const out = new Map<string, number>();
    for (const row of rows) {
      const key = groupBy === 'platform' ? row.platform : row.region;
      if (!key || key === 'ALL') continue;
      out.set(key, (out.get(key) ?? 0) + (metric.pick(row) ?? 0));
    }
    return [...out.entries()]
      .map(([name, value]) => ({ name, value }))
      .filter((b) => b.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, limit);
  }, [rows, groupBy, metric, limit]);

  if (bars.length === 0) {
    return <p className={styles.placeholderText}>No data for the current scope.</p>;
  }

  return (
    <div className={styles.chart}>
      <ResponsiveContainer width="100%" height={Math.max(120, bars.length * 34 + 16)}>
        <BarChart data={bars} layout="vertical" margin={{ top: 0, right: 12, bottom: 0, left: 0 }}>
          <XAxis type="number" hide />
          <YAxis
            type="category"
            dataKey="name"
            width={124}
            tick={{ fill: 'var(--text-secondary)', fontSize: 12 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v: string) => shortPlatform(v)}
          />
          <RechartsTooltip
            contentStyle={tooltipStyle}
            cursor={{ fill: 'var(--bg-hover)' }}
            formatter={(value: number) => [metric.format(value), metric.label]}
          />
          <Bar dataKey="value" name={metric.label} radius={[0, 4, 4, 0]} barSize={18}>
            {bars.map((bar, i) => (
              <Cell key={bar.name} fill={sliceColour(bar.name, i)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function PlatformChartBlock({ rows, block }: { rows: DataRow[]; block: Block }) {
  const { chartType, categorical } = chartSpec(block);
  if (chartType === 'donut') return <PlatformDonutBlock rows={rows} block={block} />;
  if (categorical) return <PlatformCategoryBarBlock rows={rows} block={block} />;
  return <PlatformTimeseriesBlock rows={rows} block={block} chartType={chartType} />;
}

function PlatformTimeseriesBlock({
  rows,
  block,
  chartType = 'area',
}: {
  rows: DataRow[];
  block: Block;
  chartType?: ChartType;
}) {
  const metric = metricOf(block);

  /* History rows are per region per quarter, so regions are summed back up —
     except ARPU, which is a rate and is subscriber-weighted instead. */
  const series = useMemo(() => {
    const isRate = block.options?.metric === 'arpu';
    const byQuarter = new Map<string, { value: number; weight: number }>();
    for (const row of rows) {
      const quarter = row.quarter ?? row.time_period;
      if (!quarter) continue;
      const entry = byQuarter.get(quarter) ?? { value: 0, weight: 0 };
      const raw = metric.pick(row) ?? 0;
      const weight = row.subscribers ?? 0;
      entry.value += isRate ? raw * weight : raw;
      entry.weight += weight;
      byQuarter.set(quarter, entry);
    }
    return [...byQuarter.entries()]
      .map(([quarter, v]) => ({
        quarter: prettyQuarter(quarter),
        value: isRate && v.weight > 0 ? v.value / v.weight : v.value,
      }))
      .sort((a, b) => a.quarter.localeCompare(b.quarter));
  }, [rows, metric, block.options?.metric]);

  if (series.length === 0) {
    return (
      <div className={styles.placeholder}>
        <p className={styles.placeholderText}>No data for the current scope.</p>
      </div>
    );
  }

  return (
    <div className={styles.chart}>
      <ResponsiveContainer width="100%" height={260}>
        {(() => {
          /* Axes and tooltip are identical across the three shapes, so they
             are built once rather than repeated per branch. */
          const axes = (
            <>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" vertical={false} />
              <XAxis
                dataKey="quarter"
                tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                minTickGap={24}
              />
              <YAxis
                tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                width={54}
                tickFormatter={(v: number) => metric.format(v)}
              />
              <RechartsTooltip
                contentStyle={tooltipStyle}
                formatter={(v: number) => [metric.format(v), metric.label]}
              />
            </>
          );
          const margin = { top: 8, right: 8, bottom: 0, left: 8 };

          if (chartType === 'line') {
            return (
              <LineChart data={series} margin={margin}>
                {axes}
                <Line
                  type="monotone"
                  dataKey="value"
                  name={metric.label}
                  stroke="var(--ai-accent)"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            );
          }
          if (chartType === 'bar') {
            return (
              <BarChart data={series} margin={margin}>
                {axes}
                <Bar dataKey="value" name={metric.label} fill="var(--ai-accent)" radius={[3, 3, 0, 0]} />
              </BarChart>
            );
          }
          return (
            <AreaChart data={series} margin={margin}>
              {axes}
              <Area
                type="monotone"
                dataKey="value"
                name={metric.label}
                stroke="var(--ai-accent)"
                fill="var(--ai-accent)"
                fillOpacity={0.18}
                strokeWidth={2}
              />
            </AreaChart>
          );
        })()}
      </ResponsiveContainer>
    </div>
  );
}

function PlatformDonutBlock({ rows, block }: { rows: DataRow[]; block: Block }) {
  const metric = metricOf(block);
  const groupBy = typeof block.options?.groupBy === 'string' ? block.options.groupBy : 'region';

  const slices = useMemo(() => {
    const out = new Map<string, number>();
    for (const row of rows) {
      const key = groupBy === 'platform' ? row.platform : row.region;
      if (!key || key === 'ALL') continue;
      out.set(key, (out.get(key) ?? 0) + (metric.pick(row) ?? 0));
    }
    return [...out.entries()]
      .map(([name, value]) => ({ name, value }))
      .filter((s) => s.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [rows, groupBy, metric]);

  const total = slices.reduce((acc, s) => acc + s.value, 0);

  if (slices.length === 0) {
    return (
      <div className={styles.placeholder}>
        <p className={styles.placeholderText}>No data for the current scope.</p>
      </div>
    );
  }

  return (
    <div className={styles.chart}>
      <ResponsiveContainer width="100%" height={260}>
        <PieChart>
          <Pie data={slices} dataKey="value" nameKey="name" innerRadius={62} outerRadius={96} paddingAngle={2}>
            {slices.map((slice, index) => (
              <Cell
                key={slice.name}
                fill={REGION_COLORS[slice.name] ?? `var(--data-${(index % 10) + 1})`}
                stroke="var(--bg-card)"
              />
            ))}
          </Pie>
          <Legend
            verticalAlign="middle"
            align="right"
            layout="vertical"
            iconType="circle"
            wrapperStyle={{ fontSize: 12, color: 'var(--text-secondary)' }}
          />
          <RechartsTooltip
            contentStyle={tooltipStyle}
            formatter={(v: number) => [
              `${metric.format(v)} (${formatShare(total > 0 ? v / total : 0)})`,
              metric.label,
            ]}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

function PlatformTableBlock({ rows, block }: { rows: DataRow[]; block: Block }) {
  const limit = typeof block.options?.limit === 'number' ? block.options.limit : 12;

  /* A block may name its columns; otherwise show the standard scorecard set. */
  const columns =
    Array.isArray(block.options?.columns) && block.options.columns.every((c) => typeof c === 'string')
      ? block.options.columns.filter((c): c is string => typeof c === 'string')
      : ['subscribers', 'arpu', 'revenue', 'churn_rate'];

  const ranked = useMemo(
    () =>
      [...rows]
        .filter((r) => r.platform)
        .sort((a, b) => (b.subscribers ?? 0) - (a.subscribers ?? 0))
        .slice(0, limit),
    [rows, limit]
  );

  const maxima = useMemo(() => {
    const out: Record<string, number> = {};
    for (const key of columns) {
      const metric = PLATFORM_METRICS[key];
      if (!metric) continue;
      out[key] = Math.max(...ranked.map((r) => metric.pick(r) ?? 0), 0);
    }
    return out;
  }, [ranked, columns]);

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.th}>Platform</th>
            {columns.map((key) => (
              <th key={key} className={styles.th}>
                {PLATFORM_METRICS[key]?.label ?? key}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ranked.map((row) => (
            <tr key={row.platform} className={styles.tr}>
              <td className={styles.td}>
                <span className={styles.titleCell}>
                  <PlatformIcon name={row.platform ?? ''} size={18} />
                  {shortPlatform(row.platform ?? '')}
                </span>
              </td>
              {columns.map((key) => {
                const metric = PLATFORM_METRICS[key];
                const value = metric?.pick(row) ?? 0;
                const max = maxima[key] ?? 0;
                return (
                  <td key={key} className={styles.td}>
                    <MetricCell
                      value={metric ? metric.format(value) : String(value)}
                      fraction={max > 0 ? value / max : 0}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
